// =============================================================================
// Chat Notification Repository - Manage chat mention notifications
// =============================================================================

import { supabase } from '@/integrations/supabase/client';

export interface ChatNotification {
  id: string;
  orgId: string;
  channelId: string;
  messageId: string;
  recipientMemberId: string;
  senderMemberId: string;
  type: string;
  senderName: string | null;
  channelName: string | null;
  messagePreview: string | null;
  isRead: boolean;
  createdAt: string;
}

function mapRow(row: any): ChatNotification {
  return {
    id: row.id,
    orgId: row.org_id,
    channelId: row.channel_id,
    messageId: row.message_id,
    recipientMemberId: row.recipient_member_id,
    senderMemberId: row.sender_member_id,
    type: row.type,
    senderName: row.sender_name,
    channelName: row.channel_name,
    messagePreview: row.message_preview,
    isRead: row.is_read,
    createdAt: row.created_at,
  };
}

// ─── FETCH ──────────────────────────────────────────────────────────────
export async function fetchChatNotifications(limit = 50): Promise<ChatNotification[]> {
  const { data, error } = await supabase
    .from('chat_notifications')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching chat notifications:', error);
    return [];
  }
  return (data || []).map(mapRow);
}

// ─── COUNT UNREAD ───────────────────────────────────────────────────────
export async function countUnreadChatNotifications(): Promise<number> {
  const { count, error } = await supabase
    .from('chat_notifications')
    .select('*', { count: 'exact', head: true })
    .eq('is_read', false);

  if (error) {
    console.error('Error counting chat notifications:', error);
    return 0;
  }
  return count || 0;
}

// ─── MARK READ ──────────────────────────────────────────────────────────
export async function markChatNotificationRead(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('chat_notifications')
    .update({ is_read: true })
    .eq('id', id);
  return !error;
}

export async function markAllChatNotificationsRead(): Promise<boolean> {
  const { error } = await supabase
    .from('chat_notifications')
    .update({ is_read: true })
    .eq('is_read', false);
  return !error;
}

// ─── DELETE ─────────────────────────────────────────────────────────────
export async function deleteChatNotification(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('chat_notifications')
    .delete()
    .eq('id', id);
  return !error;
}

// ─── CREATE MENTION NOTIFICATIONS ───────────────────────────────────────
export async function createMentionNotifications(params: {
  orgId: string;
  channelId: string;
  messageId: string;
  senderMemberId: string;
  senderName: string;
  channelName: string;
  messageContent: string;
  mentionedMemberIds: string[];
}): Promise<void> {
  const { orgId, channelId, messageId, senderMemberId, senderName, channelName, messageContent, mentionedMemberIds } = params;

  // Filter out sender (don't notify yourself)
  const recipients = mentionedMemberIds.filter(id => id !== senderMemberId);
  if (recipients.length === 0) return;

  const preview = messageContent.length > 100 ? messageContent.slice(0, 100) + '…' : messageContent;

  const rows = recipients.map(recipientMemberId => ({
    org_id: orgId,
    channel_id: channelId,
    message_id: messageId,
    recipient_member_id: recipientMemberId,
    sender_member_id: senderMemberId,
    type: 'mention',
    sender_name: senderName,
    channel_name: channelName,
    message_preview: preview,
  }));

  const { error } = await supabase.from('chat_notifications').insert(rows);
  if (error) {
    console.error('Error creating mention notifications:', error);
  }
}

// ─── RESOLVE MENTIONS → MEMBER IDS ─────────────────────────────────────
/**
 * Parse @Name mentions from message content and resolve them to org_member_ids
 * using the provided members list (already loaded in the composer).
 *
 * The composer inserts mentions as `@Full Name ` (terminated by a space, end-of-string,
 * line break or punctuation). We must therefore match the longest member name first,
 * to handle multi-word names correctly (e.g. "Jean-Pierre Le Goff").
 */
export function resolveMentionedMemberIds(
  content: string,
  members: Array<{ member_id: string; full_name: string | null; email: string }>
): string[] {
  if (!content || members.length === 0) return [];

  // Normalize accents/casing so "Jérôme" matches "jerome" etc.
  const normalize = (s: string) =>
    s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

  const lowerContent = content.toLowerCase();
  const normalizedContent = normalize(content);

  // Build candidates: full name, email local-part. Sort by length DESC so we
  // match the longest name first ("jean pierre" before "jean").
  type Candidate = { memberId: string; needle: string; needleNorm: string };
  const candidates: Candidate[] = [];
  for (const m of members) {
    const full = (m.full_name || '').trim();
    if (full) {
      candidates.push({ memberId: m.member_id, needle: full.toLowerCase(), needleNorm: normalize(full) });
    }
    const local = m.email.toLowerCase().split('@')[0];
    if (local) {
      candidates.push({ memberId: m.member_id, needle: local, needleNorm: normalize(local) });
    }
  }
  candidates.sort((a, b) => b.needle.length - a.needle.length);

  // Walk through every "@..." occurrence and try to match the longest candidate
  // that immediately follows. We mark consumed ranges to avoid double-matches
  // (e.g. "@Jean Pierre" must not also match "@Jean").
  const resolved = new Set<string>();
  const consumed: Array<[number, number]> = [];
  const isInsideConsumed = (pos: number) =>
    consumed.some(([s, e]) => pos >= s && pos < e);

  const atRegex = /@/g;
  let m: RegExpExecArray | null;
  while ((m = atRegex.exec(lowerContent)) !== null) {
    const atPos = m.index;
    if (isInsideConsumed(atPos)) continue;
    // @ must start of string or be preceded by whitespace/newline/punctuation
    const prev = atPos > 0 ? lowerContent[atPos - 1] : ' ';
    if (!/\s|[(\[<,;:!?]/.test(prev) && atPos !== 0) continue;

    const afterAt = atPos + 1;
    const slice = lowerContent.slice(afterAt);
    const sliceNorm = normalizedContent.slice(afterAt);

    const hit = candidates.find(c => {
      const matchedAccented = slice.startsWith(c.needle);
      const matchedNormalized = sliceNorm.startsWith(c.needleNorm);
      if (!matchedAccented && !matchedNormalized) return false;
      const len = matchedAccented ? c.needle.length : c.needleNorm.length;
      // Boundary: end-of-string or non-name character
      const next = slice.charAt(len);
      return next === '' || /[\s.,;:!?)\]>\n]/.test(next);
    });

    if (hit) {
      resolved.add(hit.memberId);
      const len = slice.startsWith(hit.needle) ? hit.needle.length : hit.needleNorm.length;
      consumed.push([atPos, afterAt + len]);
    }
  }

  return Array.from(resolved);
}
